import type { Portfolio } from "@/types/Portfolio";
import type { ProvisioningPlan } from "@/onboarding/concierge/types";
import type { AssetClassId } from "../types";

/**
 * Recorded smart-watchlist output, replayed to logged-out visitors.
 *
 * The concierge cannot be called from the demo at all — not as a cost
 * decision, but because backend-apps/concierge/app/auth.py guards /plan with
 * require_subscriber: 401 unauthenticated, 403 without the subscriber role.
 * Its own comment is "the concierge spends API credits", and an anonymous
 * endpoint that spent them would be an obvious thing to abuse.
 *
 * So we record real output once per desk and replay it, labelled as a saved
 * example. What a visitor sees is genuinely what the product produces, because
 * it was produced by it.
 *
 * Watchlist mode only. Onboarding mode builds a dashboard, which the demo
 * templates already do for free and instantly — recording both would have put
 * two features that lay out the same screen next to each other and made both
 * look redundant.
 *
 * Generate with: npm run record:demo-plans -- <token>
 */
export type RecordedWatchlist = {
  /** ISO timestamp of the recording run. */
  recordedAt: string;
  assetClass: AssetClassId;
  /** The prompt that produced it — shown to the visitor, and reproducible. */
  query: string;
  /** Echoed from the response so a schema change is detectable. */
  schemaVersion: number;
  catalogVersion: string;
  plan: ProvisioningPlan;
  /**
   * NEWSQUAWK library lists the plan cites via `lib:` refs, resolved to their
   * real contents at record time. PlanPreview normally resolves these from the
   * signed-in user's own fetch, which is empty here — without this the reuse
   * rows render as names with no terms behind them.
   */
  library: Portfolio[];
};

/**
 * Eager glob: the fixture set has to be known synchronously, because the
 * guided run drops its last step when there is no recording for that desk.
 */
const modules = import.meta.glob<{ default: RecordedWatchlist }>("./*.json", {
  eager: true,
});

const byAssetClass = new Map<string, RecordedWatchlist>();
for (const mod of Object.values(modules)) {
  const recorded = mod.default;
  if (recorded?.assetClass) byAssetClass.set(recorded.assetClass, recorded);
}

export function getRecordedWatchlist(
  assetClass: AssetClassId
): RecordedWatchlist | null {
  return byAssetClass.get(assetClass) ?? null;
}

export function hasRecordedWatchlist(assetClass: AssetClassId): boolean {
  return byAssetClass.has(assetClass);
}

export const ALL_RECORDED_WATCHLISTS: RecordedWatchlist[] = [
  ...byAssetClass.values(),
];
